import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Inject,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  PLATFORM_ID,
  SimpleChanges,
  signal,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { gsap } from 'gsap';

/**
 * Icon geometry, kept as raw primitives so a single generic <svg> template can
 * draw all 30 glyphs — cheaper than 30 inline sprites in the markup.
 *   p — path `d`            c — [cx, cy, r]        e — [cx, cy, rx, ry, rotate]
 */
export interface MegaIcon {
  p?: string[];
  c?: [number, number, number][];
  e?: [number, number, number, number, number][];
}

export interface MegaItem {
  title: string;
  desc: string;
  link: string;
  icon: MegaIcon;
}

export interface MegaColumn {
  title: string;
  accent: 'pink' | 'violet' | 'blue' | 'blend';
  icon: MegaIcon;
  items: MegaItem[];
}

const ICONS = {
  globe: {
    c: [[12, 12, 8.6]],
    p: ['M3.4 12h17.2', 'M12 3.4a13.5 13.5 0 0 1 0 17.2 13.5 13.5 0 0 1 0-17.2'],
  },
  palette: {
    p: [
      'M12 3.2a8.8 8.8 0 1 0 0 17.6c.92 0 1.55-.7 1.55-1.5 0-.4-.15-.74-.4-1a1.4 1.4 0 0 1 1-2.4h1.75a4.9 4.9 0 0 0 4.9-4.9c0-4.35-4.03-7.8-8.8-7.8Z',
    ],
    c: [[7.8, 11.6, 1.05], [12, 7.6, 1.05], [16.2, 11.2, 1.05]],
  },
  building: {
    p: [
      'M4.5 20.5V4.9A1.4 1.4 0 0 1 5.9 3.5h7.7a1.4 1.4 0 0 1 1.4 1.4v15.6',
      'M15 9.6h3.6a1.4 1.4 0 0 1 1.4 1.4v9.5',
      'M3 20.5h18',
      'M8 7.5h3.5M8 11h3.5M8 14.5h3.5',
    ],
  },
  appWindow: {
    p: [
      'M3.2 6.8A2.6 2.6 0 0 1 5.8 4.2h12.4a2.6 2.6 0 0 1 2.6 2.6v10.4a2.6 2.6 0 0 1-2.6 2.6H5.8a2.6 2.6 0 0 1-2.6-2.6Z',
      'M3.2 9.4h17.6',
      'M6.3 6.8h.01M9.1 6.8h.01',
    ],
  },
  cart: {
    p: ['M2.8 4h2.1l2.4 10.3a1.9 1.9 0 0 0 1.85 1.45h7a1.9 1.9 0 0 0 1.85-1.4L20 7.6H6'],
    c: [[9.2, 19.4, 1.35], [17, 19.4, 1.35]],
  },
  server: {
    p: [
      'M4 5.6A1.6 1.6 0 0 1 5.6 4h12.8A1.6 1.6 0 0 1 20 5.6v3.2a1.6 1.6 0 0 1-1.6 1.6H5.6A1.6 1.6 0 0 1 4 8.8Z',
      'M4 15.2a1.6 1.6 0 0 1 1.6-1.6h12.8a1.6 1.6 0 0 1 1.6 1.6v3.2a1.6 1.6 0 0 1-1.6 1.6H5.6A1.6 1.6 0 0 1 4 18.4Z',
      'M7.4 7.2h.01M7.4 16.8h.01',
    ],
  },
  wrench: {
    p: [
      'M14.9 6.2a1 1 0 0 0 0 1.4l1.5 1.5a1 1 0 0 0 1.4 0l3.55-3.55a6 6 0 0 1-7.95 7.95l-6.6 6.6a2.12 2.12 0 0 1-3-3l6.6-6.6a6 6 0 0 1 7.95-7.95Z',
    ],
  },
  android: {
    p: [
      'M6 10.4a6 6 0 0 1 12 0v6.2a1.3 1.3 0 0 1-1.3 1.3H7.3A1.3 1.3 0 0 1 6 16.6Z',
      'M8.1 4.9 6.9 3M15.9 4.9 17.1 3',
      'M9.6 8.2h.01M14.4 8.2h.01',
      'M9.4 20.8v-2.9M14.6 20.8v-2.9',
    ],
  },
  smartphone: {
    p: [
      'M7.6 2.6h8.8a1.6 1.6 0 0 1 1.6 1.6v15.6a1.6 1.6 0 0 1-1.6 1.6H7.6A1.6 1.6 0 0 1 6 19.8V4.2a1.6 1.6 0 0 1 1.6-1.6Z',
      'M10.6 5.4h2.8',
      'M12 18.2h.01',
    ],
  },
  layers: {
    p: ['m12 2.6 8.8 4.7-8.8 4.7-8.8-4.7Z', 'm3.2 12 8.8 4.7 8.8-4.7', 'm3.2 16.7 8.8 4.7 8.8-4.7'],
  },
  blend: {
    c: [[9.2, 9.2, 5.2], [14.8, 14.8, 5.2]],
  },
  atom: {
    c: [[12, 12, 1.9]],
    e: [[12, 12, 9.4, 4, 45], [12, 12, 9.4, 4, -45]],
  },
  devices: {
    p: [
      'M3.2 5.6A1.6 1.6 0 0 1 4.8 4h10.4a1.6 1.6 0 0 1 1.6 1.6v3.2',
      'M3.2 5.6v8.8a1.6 1.6 0 0 0 1.6 1.6h5.2',
      'M7.4 20.4h4.4M9.6 16v4.4',
      'M17.8 10.4h2.6a1.6 1.6 0 0 1 1.6 1.6v6.8a1.6 1.6 0 0 1-1.6 1.6h-2.6a1.6 1.6 0 0 1-1.6-1.6V12a1.6 1.6 0 0 1 1.6-1.6Z',
    ],
  },
  cube: {
    p: ['M12 2.4 4.2 6.7v10.6L12 21.6l7.8-4.3V6.7Z', 'M4.2 6.7 12 11l7.8-4.3', 'M12 11v10.6'],
  },
  users: {
    c: [[9.2, 7.9, 3.3]],
    p: ['M3.2 20.2a6 6 0 0 1 12 0', 'M16.4 5.3a3.3 3.3 0 0 1 0 5.4', 'M17.6 14.4a5.6 5.6 0 0 1 3.3 5.8'],
  },
  sparkle: {
    p: [
      'M11.4 3 13.1 8.1 18.2 9.8 13.1 11.5 11.4 16.6 9.7 11.5 4.6 9.8 9.7 8.1Z',
      'm18.2 15.4.75 2.05 2.05.75-2.05.75-.75 2.05-.75-2.05-2.05-.75 2.05-.75Z',
    ],
  },
  cloud: {
    p: ['M6.9 19.2a4.6 4.6 0 0 1 .35-9.19 6.1 6.1 0 0 1 11.65 1.37 3.95 3.95 0 0 1-.6 7.82Z'],
  },
  zap: {
    p: ['M13.4 2.4 4.6 13.6h6.3L10.6 21.6l8.8-11.2h-6.3Z'],
  },
  code: {
    p: ['m8.6 16.2-4.2-4.2 4.2-4.2', 'm15.4 7.8 4.2 4.2-4.2 4.2', 'M13.4 4.8 10.6 19.2'],
  },
  cpu: {
    p: [
      'M8.2 4.4h7.6a3.8 3.8 0 0 1 3.8 3.8v7.6a3.8 3.8 0 0 1-3.8 3.8H8.2a3.8 3.8 0 0 1-3.8-3.8V8.2a3.8 3.8 0 0 1 3.8-3.8Z',
      'M9.6 9.6h4.8v4.8H9.6Z',
      'M12 2.2v2.2M12 19.6v2.2M2.2 12h2.2M19.6 12h2.2',
    ],
  },
  search: {
    c: [[11, 11, 6.9]],
    p: ['m16.1 16.1 4.4 4.4'],
  },
  share: {
    c: [[6, 12, 2.7], [17.6, 6.4, 2.7], [17.6, 17.6, 2.7]],
    p: ['m8.45 10.8 6.75-3.2', 'm8.45 13.2 6.75 3.2'],
  },
  target: {
    c: [[12, 12, 8.2], [12, 12, 4.4], [12, 12, 1]],
  },
  diamond: {
    p: [
      'M11.3 2.9 2.9 11.3a1 1 0 0 0 0 1.4l8.4 8.4a1 1 0 0 0 1.4 0l8.4-8.4a1 1 0 0 0 0-1.4l-8.4-8.4a1 1 0 0 0-1.4 0Z',
      'm8.4 12 3.6-3.6 3.6 3.6-3.6 3.6Z',
    ],
  },
  pen: {
    p: ['m12.6 18.6 6.4-6.4 2.6 2.6-6.4 6.4Z', 'm18.2 12.4-1.4-6.9L2.6 2.6l2.9 14.2 6.9 1.4 5.8-5.8Z', 'm2.6 2.6 6.55 6.55'],
    c: [[10.6, 10.6, 1.9]],
  },
  shapes: {
    p: ['m7 2.8 4.1 6.7H2.9Z', 'M13.6 13.6h7.6v7.6h-7.6Z'],
    c: [[7, 17.4, 3.8]],
  },
  video: {
    p: [
      'M2.8 7.8a2.6 2.6 0 0 1 2.6-2.6h6.6a2.6 2.6 0 0 1 2.6 2.6v8.4a2.6 2.6 0 0 1-2.6 2.6H5.4a2.6 2.6 0 0 1-2.6-2.6Z',
      'm14.6 10.6 6.6-3.6v10l-6.6-3.6Z',
    ],
  },
  megaphone: {
    p: [
      'M3.2 10.6v2.8a1.3 1.3 0 0 0 1.3 1.3h1.9l5.4 3.9V5.4L6.4 9.3H4.5a1.3 1.3 0 0 0-1.3 1.3Z',
      'M15.6 8.6a4.9 4.9 0 0 1 0 6.8',
      'M18.6 5.8a8.8 8.8 0 0 1 0 12.4',
      'M6.4 14.7v3.9a1.4 1.4 0 0 0 2.8 0v-2.4',
    ],
  },
  mail: {
    p: [
      'M3.2 7.2A1.8 1.8 0 0 1 5 5.4h14a1.8 1.8 0 0 1 1.8 1.8v9.6a1.8 1.8 0 0 1-1.8 1.8H5a1.8 1.8 0 0 1-1.8-1.8Z',
      'm3.6 7 8.4 5.6L20.4 7',
    ],
  },
  idCard: {
    c: [[9, 10.6, 2.2]],
    p: [
      'M3.2 6.6A1.8 1.8 0 0 1 5 4.8h14a1.8 1.8 0 0 1 1.8 1.8v10.8a1.8 1.8 0 0 1-1.8 1.8H5a1.8 1.8 0 0 1-1.8-1.8Z',
      'M5.8 16.4a3.4 3.4 0 0 1 6.4 0',
      'M14.6 9.4h4M14.6 12.8h4',
    ],
  },
} satisfies Record<string, MegaIcon>;

@Component({
  selector: 'app-services-mega-menu',
  templateUrl: './services-mega-menu.component.html',
  styleUrls: ['./services-mega-menu.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ServicesMegaMenuComponent implements OnChanges, OnDestroy {
  /** Driven by the header — hover intent or a click on the Services trigger. */
  @Input() open = false;

  /** Pointer entered/left the panel; the header uses these to hold it open. */
  @Output() hoverIn = new EventEmitter<void>();
  @Output() hoverOut = new EventEmitter<void>();
  /** Scrim click, Escape, or a link navigation. */
  @Output() closed = new EventEmitter<void>();

  @HostBinding('class.is-open') get isOpen() {
    return this.visible();
  }
  @HostBinding('attr.aria-hidden') get ariaHidden() {
    return this.visible() ? null : 'true';
  }

  /** Nothing is rendered until the menu is opened for the first time. */
  readonly mounted = signal(false);
  readonly visible = signal(false);

  /**
   * Ambient dust. Generated from a pure formula rather than Math.random() so
   * the SSR markup and the hydrated markup agree.
   */
  readonly particles = Array.from({ length: 22 }, (_, i) => ({
    x: (i * 37.6) % 97 + 1.5,
    y: (i * 61.3) % 88 + 5,
    s: 1.5 + ((i * 7) % 5) * 0.7,
  }));

  /**
   * The four groups the original Services flyout carried, with their original
   * headings, item labels, order and routes preserved exactly. Nothing here is
   * renamed or dropped — the redesign only changes how they are presented.
   */
  readonly columns: MegaColumn[] = [
    {
      title: 'Web solution',
      accent: 'pink',
      icon: ICONS.globe,
      items: [
        {
          title: 'Website Designing',
          desc: 'Creative, responsive design',
          link: '/service/website-design',
          icon: ICONS.palette,
        },
        {
          title: 'Web Development',
          desc: 'Modern, scalable websites',
          link: '/service/web-development',
          icon: ICONS.code,
        },
        {
          title: 'Web Application Development',
          desc: 'Powerful custom platforms',
          link: '/service/web-application-development',
          icon: ICONS.appWindow,
        },
        {
          title: 'Web Hosting Solutions',
          desc: 'Reliable, fast hosting',
          link: '/service/web-hosting-solutions',
          icon: ICONS.server,
        },
      ],
    },
    {
      title: 'App solution',
      accent: 'violet',
      icon: ICONS.smartphone,
      items: [
        {
          title: 'Android App Development',
          desc: 'High performance Android apps',
          link: '/service/android-app-development',
          icon: ICONS.android,
        },
        {
          title: 'Ios App Development',
          desc: 'Secure, scalable iOS apps',
          link: '/service/ios-app-development',
          icon: ICONS.smartphone,
        },
        {
          title: 'Hybrid App Development',
          desc: 'One code, multiple platforms',
          link: '/service/hybrid-app-development',
          icon: ICONS.blend,
        },
        {
          title: 'Native App Development',
          desc: 'Fast, powerful, reliable apps',
          link: '/service/native-app-development',
          icon: ICONS.devices,
        },
      ],
    },
    {
      title: 'Digital Marketing',
      accent: 'blue',
      icon: ICONS.megaphone,
      items: [
        {
          title: 'Search Engine Optimisation',
          desc: 'Rank higher, get found',
          link: '/service/search-engine-optimisation',
          icon: ICONS.search,
        },
        {
          title: 'Digital Media Marketing',
          desc: 'Reach your target audience',
          link: '/service/digital-media-marketing',
          icon: ICONS.target,
        },
        {
          title: 'Social Media Promotion',
          desc: 'Boost brand awareness',
          link: '/service/social-media-promotion',
          icon: ICONS.share,
        },
        {
          title: 'Bulk Sms & Email Marketing',
          desc: 'Smart, effective campaigns',
          link: '/service/bulk-sms-and-email-marketing',
          icon: ICONS.mail,
        },
      ],
    },
    {
      title: 'More Services',
      accent: 'blend',
      icon: ICONS.sparkle,
      items: [
        {
          title: 'Logo Design',
          desc: 'Unique, memorable logos',
          link: '/service/logo-design',
          icon: ICONS.shapes,
        },
        {
          title: 'Graphics Design',
          desc: 'Creative visual designs',
          link: '/service/graphic-design',
          icon: ICONS.pen,
        },
        {
          title: 'Digital Visiting Card',
          desc: 'Professional and modern',
          link: '/service/digital-visiting-card',
          icon: ICONS.idCard,
        },
        {
          title: 'Video Creation',
          desc: 'Engaging, impactful videos',
          link: '/service/video-creation',
          icon: ICONS.video,
        },
      ],
    },
  ];

  private readonly isBrowser: boolean;
  private ctx?: gsap.Context;
  private ambient: gsap.core.Tween[] = [];
  private parallax: Array<{
    x: (v: number) => void;
    y: (v: number) => void;
    depth: number;
  }> = [];
  private detachPointer?: () => void;

  constructor(
    private host: ElementRef<HTMLElement>,
    private zone: NgZone,
    private cdr: ChangeDetectorRef,
    @Inject(PLATFORM_ID) platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['open'] || !this.isBrowser) return;
    this.open ? this.show() : this.hide();
  }

  ngOnDestroy(): void {
    this.teardown();
    this.ctx?.revert();
  }

  requestClose(): void {
    this.closed.emit();
  }

  // ── animation ────────────────────────────────────────────────────────────

  private show(): void {
    this.mounted.set(true);
    this.visible.set(true);
    this.cdr.markForCheck();

    // The whole panel lives behind @if, and this runs from ngOnChanges — the
    // view is not rendered until the current change-detection pass completes,
    // so querying GSAP targets any earlier finds nothing. Wait one frame.
    // The panel is opacity:0 until GSAP takes over, so there is no flash.
    this.zone.runOutsideAngular(() =>
      requestAnimationFrame(() => {
        if (!this.visible()) return; // closed again before the frame landed
        this.anchorToNavbar();
        this.playOpen();
      })
    );
  }

  private hide(): void {
    if (!this.visible()) return;
    this.zone.runOutsideAngular(() => this.playClose());
  }

  /**
   * The navbar is a floating pill that shrinks on scroll, so its bottom edge
   * moves. Measure it at open time rather than hard-coding an offset.
   */
  private anchorToNavbar(): void {
    const nav = document.querySelector<HTMLElement>('nav.navbar');
    const top = nav ? nav.getBoundingClientRect().bottom + 14 : 108;
    this.host.nativeElement.style.setProperty('--mm-top', `${Math.round(top)}px`);
  }

  private playOpen(): void {
    const root = this.host.nativeElement;
    this.ctx ??= gsap.context(() => {}, root);

    this.ctx.add(() => {
      gsap.killTweensOf(['.mm-scrim', '.mm-shell']);

      const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

      tl.fromTo(
        '.mm-scrim',
        { autoAlpha: 0 },
        { autoAlpha: 1, duration: 0.35, ease: 'power2.out' },
        0
      )
        .fromTo(
          '.mm-surface',
          { autoAlpha: 0, scale: 0.98 },
          { autoAlpha: 1, scale: 1, duration: 0.35 },
          0
        )
        .fromTo(
          '.mm-shell',
          { autoAlpha: 0, y: -15 },
          { autoAlpha: 1, y: 0, duration: 0.45 },
          0.02
        )
        .fromTo(
          '.mm-promo',
          { autoAlpha: 0, x: -40 },
          { autoAlpha: 1, x: 0, duration: 0.5 },
          0.08
        )
        .fromTo(
          '.mm-col',
          { autoAlpha: 0, y: 20 },
          { autoAlpha: 1, y: 0, duration: 0.45, stagger: 0.08 },
          0.12
        );

      // Items stagger *within* their own column so the four columns cascade in
      // parallel — staggering the flat list of 25 would run for over a second.
      gsap.utils.toArray<HTMLElement>('.mm-col').forEach((col, i) => {
        const at = 0.18 + i * 0.08;
        // The <li> wrapper is animated, not the <a> — the anchor owns the CSS
        // hover transform and GSAP's inline transform would outrank it.
        tl.fromTo(
          col.querySelectorAll('.mm-reveal'),
          { autoAlpha: 0, y: 14 },
          { autoAlpha: 1, y: 0, duration: 0.4, stagger: 0.04 },
          at
        ).fromTo(
          col.querySelectorAll('.mm-glyph-pop'),
          { scale: 0.8, rotate: -10, autoAlpha: 0 },
          {
            scale: 1,
            rotate: 0,
            autoAlpha: 1,
            duration: 0.4,
            stagger: 0.04,
            ease: 'back.out(2)',
          },
          at + 0.02
        );
      });

      tl.fromTo(
        '.mm-pop',
        { scale: 0.92, autoAlpha: 0 },
        { scale: 1, autoAlpha: 1, duration: 0.42, stagger: 0.06 },
        0.3
      );
    });

    this.startAmbient();
  }

  private playClose(): void {
    if (!this.ctx) {
      // Closed before the open frame ever landed — nothing to unwind.
      this.zone.run(() => {
        this.visible.set(false);
        this.cdr.markForCheck();
      });
      return;
    }

    this.ctx.add(() => {
      gsap.killTweensOf(['.mm-scrim', '.mm-shell']);
      // Spec asks for a reversed feel but a single collective fade — staggering
      // 30 items back out reads as sluggish at this size.
      gsap.to('.mm-shell', {
        autoAlpha: 0,
        y: -10,
        duration: 0.3,
        ease: 'power2.inOut',
        onComplete: () => {
          this.pauseAmbient();
          this.zone.run(() => {
            this.visible.set(false);
            this.cdr.markForCheck();
          });
        },
      });
      gsap.to('.mm-scrim', { autoAlpha: 0, duration: 0.3, ease: 'power2.inOut' });
    });
  }

  // ── ambient loops + pointer parallax ─────────────────────────────────────

  private startAmbient(): void {
    if (this.ambient.length) {
      this.ambient.forEach((t) => t.resume());
      return;
    }
    const root = this.host.nativeElement;

    this.ctx?.add(() => {
      // Illustration breathing.
      this.ambient.push(
        gsap.to('.mm-ill', {
          y: 8,
          duration: 5,
          ease: 'sine.inOut',
          repeat: -1,
          yoyo: true,
          startAt: { y: -8 },
        })
      );

      // Gradient blobs: extremely slow, never in sync with each other.
      gsap.utils.toArray<HTMLElement>('.mm-blob').forEach((blob, i) => {
        this.ambient.push(
          gsap.to(blob, {
            xPercent: i % 2 ? -14 : 16,
            yPercent: i % 2 ? 12 : -10,
            scale: 1.14,
            duration: 20 + i * 3,
            ease: 'sine.inOut',
            repeat: -1,
            yoyo: true,
          })
        );
      });

      // Dust drift.
      gsap.utils.toArray<HTMLElement>('.mm-particle').forEach((dot, i) => {
        this.ambient.push(
          gsap.to(dot, {
            x: ((i * 13) % 40) - 20,
            y: ((i * 29) % 46) - 26,
            opacity: 0.15 + ((i * 11) % 6) / 10,
            duration: 9 + (i % 7),
            ease: 'sine.inOut',
            repeat: -1,
            yoyo: true,
            delay: (i % 9) * 0.35,
          })
        );
      });

      // A faint travelling sheen along the hairlines.
      this.ambient.push(
        gsap.to('.mm-line', {
          strokeDashoffset: -420,
          duration: 14,
          ease: 'none',
          repeat: -1,
          stagger: 2.5,
        })
      );
    });

    this.bindParallax(root);
  }

  private pauseAmbient(): void {
    this.ambient.forEach((t) => t.pause());
  }

  /**
   * Pointer parallax. Only ten elements are driven — quickTo writes straight to
   * the transform without allocating a tween per move, so this stays cheap.
   */
  private bindParallax(root: HTMLElement): void {
    if (this.detachPointer) return;
    const shell = root.querySelector<HTMLElement>('.mm-shell');
    if (!shell) return;

    const register = (selector: string, depth: number) =>
      gsap.utils.toArray<HTMLElement>(root.querySelectorAll(selector)).forEach((el) =>
        this.parallax.push({
          x: gsap.quickTo(el, 'x', { duration: 0.7, ease: 'power3.out' }),
          y: gsap.quickTo(el, 'y', { duration: 0.7, ease: 'power3.out' }),
          depth: Number(el.dataset['depth']) || depth,
        })
      );

    register('.mm-blob', 5);
    register('.mm-ill-layer', 12);
    register('.mm-col-icon', 3);

    let queued = false;
    const onMove = (e: MouseEvent) => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        const r = shell.getBoundingClientRect();
        const nx = (e.clientX - r.left) / r.width - 0.5;
        const ny = (e.clientY - r.top) / r.height - 0.5;
        for (const p of this.parallax) {
          p.x(nx * p.depth * 2);
          p.y(ny * p.depth * 2);
        }
      });
    };
    const onLeave = () => this.parallax.forEach((p) => (p.x(0), p.y(0)));

    shell.addEventListener('mousemove', onMove, { passive: true });
    shell.addEventListener('mouseleave', onLeave, { passive: true });
    this.detachPointer = () => {
      shell.removeEventListener('mousemove', onMove);
      shell.removeEventListener('mouseleave', onLeave);
    };
  }

  private teardown(): void {
    this.detachPointer?.();
    this.detachPointer = undefined;
    this.ambient.forEach((t) => t.kill());
    this.ambient = [];
    this.parallax = [];
  }
}
